/**
 * Department API Service
 * Handles department CRUD operations with backend
 */

import { apiGet, apiPost, apiPut, apiDelete } from '@/shared/api/apiClient';
import type { PagedResult } from '@/types';

/**
 * Department DTOs matching backend models
 */
export interface DepartmentDto {
  id: string;
  name: string;
  code: string | null;
  description: string | null;
  parentDepartmentId: string | null;
  parentDepartmentName: string | null;
  headEmployeeId: string | null;
  headEmployeeName: string | null;
  tenantId: string;
  createdAt: string;
  updatedAt: string | null;
}

export interface DepartmentListDto {
  id: string;
  name: string;
  code: string | null;
  description: string | null;
  parentDepartmentId: string | null;
  parentDepartmentName: string | null;
}

export interface CreateDepartmentRequest {
  name: string;
  code?: string;
  description?: string;
  parentDepartmentId?: string;
  headEmployeeId?: string;
  tenantId: string;
}

export interface UpdateDepartmentRequest {
  name: string;
  code?: string;
  description?: string;
  parentDepartmentId?: string;
  headEmployeeId?: string;
  tenantId: string;
}

/**
 * Get all departments for a tenant
 */
export async function getAllDepartments(tenantId: string): Promise<DepartmentListDto[]> {
  return apiGet<DepartmentListDto[]>(`/departments?tenantId=${tenantId}`);
}

export interface DepartmentListParams {
  tenantId: string;
  /** 0-based row offset. */
  offset?: number;
  limit?: number;
  search?: string;
}

export interface DepartmentListResult {
  items: DepartmentListDto[];
  totalCount: number;
}

/**
 * Server-side paginated/searchable variant of `getAllDepartments`, used only by
 * the Configuration screen's table. `getAllDepartments` above is left untouched —
 * it still serves the parent-department dropdown (and other screens) unpaginated.
 */
export async function getDepartmentsPaged(params: DepartmentListParams): Promise<DepartmentListResult> {
  const query = new URLSearchParams({ tenantId: params.tenantId });
  if (params.offset !== undefined) query.append('offset', String(params.offset));
  if (params.limit !== undefined) query.append('limit', String(params.limit));
  if (params.search) query.append('search', params.search);

  const data = await apiGet<PagedResult<DepartmentListDto>>(`/departments?${query.toString()}`);
  return { items: data.items ?? [], totalCount: data.totalCount ?? 0 };
}

/**
 * Get department by ID
 */
export async function getDepartmentById(id: string, tenantId: string): Promise<DepartmentDto> {
  return apiGet<DepartmentDto>(`/departments/${id}?tenantId=${tenantId}`);
}

/**
 * Create new department
 */
export async function createDepartment(data: CreateDepartmentRequest): Promise<{ id: string }> {
  return apiPost<{ id: string }>('/departments', data);
}

/**
 * Update existing department
 */
export async function updateDepartment(id: string, data: UpdateDepartmentRequest): Promise<void> {
  return apiPut<void>(`/departments/${id}`, data);
}

/**
 * Delete department
 */
export async function deleteDepartment(id: string, tenantId: string): Promise<void> {
  return apiDelete<void>(`/departments/${id}`, { tenantId });
}
